import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import FlownectLogo from '../../../assets/images/flownectlogo.png';
import { useExtendedLoading } from '../../shared/hooks/useExtendedLoading';
import { useDraftsMutations } from '../drafts/hooks/useCreateDrafts';

const QUOTES = [
    'Your brand speaks before you do.',
    "Great marketing doesn't interrupt. It attracts.",
    'Every click is a conversation waiting to happen.',
    'The best campaigns feel like they were made for one person.',
    'Visibility without strategy is just noise.',
    'Your audience is out there. Meet them where they are.',
    'Content that connects converts.',
    'A strong brand is the shortest path to trust.',
    "Impressions fade. Impact doesn't.",
    'Fewer tools. Better results.',
    'The fastest workflow is the one you actually use.',
    'Done beats perfect. Ship it.',
    'Clarity is the ultimate productivity hack.',
    'Automate the routine. Focus on what matters.',
    'Your best work happens when friction disappears.',
    'Build systems, not habits. Systems scale.',
    'Speed is a feature. Treat it like one.',
    "Efficiency isn't about doing more. It's about doing right.",
    'Streamline once. Benefit forever.',
    'Complexity is the enemy of execution.',
    'Less friction, more flow.',
    'Optimize for outcomes, not output.',
    'Small improvements compound into massive results.',
    'The right tool at the right moment changes everything.',
];

type LoadingState =
    | {
        mode: 'create';
        templateId: string;
        projectName: string;
        content: { html: string; css: string };
        companyId: string | number;
    }
    | {
        mode: 'open';
        draftId: string;
        projectType: 'draft' | 'publish';
    };

export function PageBuilderLoadingScreen() {
    const navigate = useNavigate();
    const location = useLocation() as any;

    const [quote] = useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);
    const [progress, setProgress] = useState(0);
    const [mutationDone, setMutationDone] = useState(false);

    const startedRef = useRef(false);
    const pendingDraftId = useRef<string | null>(null);
    const pendingType = useRef<'draft' | 'publish'>('draft');

    const state = location.state as LoadingState | undefined;

    const { create } = useDraftsMutations();

    const showLoading = useExtendedLoading(!mutationDone, 1000);

    useEffect(() => {
        if (!mutationDone || !pendingDraftId.current) return;

        const t = setTimeout(() => {
            navigate(`/business/page-builder/editor/${pendingDraftId.current}?type=${pendingType.current}`, {
                replace: true,
            });
        }, 1000);

        return () => clearTimeout(t);
    }, [mutationDone, navigate]);

    useEffect(() => {
        const id = setInterval(() => {
            if (mutationDone) return;
            setProgress((p) => (p < 85 ? +(Math.min(p + (85 - p) * 0.05 + 0.4, 85)).toFixed(1) : p));
        }, 80);
        return () => clearInterval(id);
    }, [mutationDone]);

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;

        if (!state?.mode) {
            navigate('/business/page-builder', { replace: true });
            return;
        }

        if (state.mode === 'open') {
            if (!state.draftId) {
                navigate('/business/page-builder', { replace: true });
                return;
            }
            pendingDraftId.current = state.draftId;
            pendingType.current = state.projectType ?? 'draft';
            setProgress(100);
            setMutationDone(true);
            return;
        }

        if (!state.templateId || !state.projectName || !state.content) {
            navigate('/business/page-builder', { replace: true });
            return;
        }

        create.mutate(
            {
                projectName: state.projectName,
                templateId: state.templateId,
                content: state.content,
                meta: { companyId: state.companyId },
            },
            {
                onSuccess: (draft: any) => {
                    const realDraft = draft?.data?.id ? draft.data : draft;
                    pendingDraftId.current = realDraft.id;
                    pendingType.current = 'draft';
                    setProgress(100);
                    setMutationDone(true);
                },
                onError: () => {
                    navigate('/business/page-builder', { replace: true });
                },
            },
        );
    }, [navigate]);

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--fx-surface-base, #FFFFFF)',
                zIndex: 2000,
            }}
        >
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 18, // ↓ un poco menos para que “Brand” quede pegado al spinner
                    padding: '48px 56px',
                    borderRadius: 20,
                    background: 'var(--fx-surface-base, #FFFFFF)',
                    boxShadow: '0 2px 24px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                    width: 360,
                }}
            >
                {/* Spinner + logo */}
                <div
                    style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <motion.svg
                        width="96"
                        height="96"
                        viewBox="0 0 96 96"
                        fill="none"
                        style={{ position: 'absolute' }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
                    >
                        <circle cx="48" cy="48" r="44" stroke="#FFE0B8" strokeWidth="2" />
                        <circle
                            cx="48"
                            cy="48"
                            r="44"
                            stroke="#FB8E00"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeDasharray="55 222"
                        />
                    </motion.svg>

                    <div
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            background: 'var(--fx-surface-sunken, #F8FAFC)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        <img
                            src={FlownectLogo}
                            alt="Flownect"
                            style={{ width: 44, height: 44, objectFit: 'contain' }}
                        />
                    </div>
                </div>

                {/* ✅ Brand text centrado: abajo del spinner y arriba del quote */}
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.45 }}
                    style={{
                        textAlign: 'center',
                        marginTop: 2,
                        marginBottom: 6,
                    }}
                >
                    <span
                        style={{
                            fontFamily: "Cormorant Garamond, Georgia, serif",
                            fontSize: 28,
                            fontWeight: 300,
                            letterSpacing: '0.06em',
                            color: 'var(--fx-surface-text, #0F172A)',
                            lineHeight: 1,
                            display: 'inline-block',
                        }}
                    >
                        Flownect
                    </span>
                </motion.div>

                {/* Quote */}
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    style={{
                        margin: 0,
                        fontSize: 13,
                        color: 'var(--fx-surface-textsecondary, #64748B)',
                        textAlign: 'center',
                        lineHeight: 1.65,
                        maxWidth: 220,
                    }}
                >
                    {quote}
                </motion.p>

                {/* Progress bar */}
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                    <div
                        style={{
                            width: '100%',
                            height: 3,
                            borderRadius: 99,
                            background: 'var(--fx-surface-sunken, #F8FAFC)',
                            overflow: 'hidden',
                        }}
                    >
                        <motion.div
                            style={{
                                height: '100%',
                                borderRadius: 99,
                                background: 'linear-gradient(90deg, #FC9700, #FB8E00)',
                                originX: 0,
                            }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.35, ease: 'easeOut' }}
                        />
                    </div>

                    <span style={{ fontSize: 11, color: 'var(--fx-surface-texttertiary, #94A3B8)', textAlign: 'right' }}>
                        {mutationDone ? 'Almost ready…' : state?.mode === 'open' ? 'Opening project…' : 'Creating draft…'}
                    </span>
                </div>
            </motion.div>
        </div>

    );
}
